<template>
  <div>
    <b-card>
      <template #header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <h4 class="mb-0">Akıllı Oluştur</h4>
          <b-button variant="outline-secondary" size="sm" :to="{ name: 'Fleet' }">Listeye dön</b-button>
        </div>
      </template>

      <b-nav pills class="mb-2">
        <b-nav-item v-for="(s, i) in steps" :key="s" :active="step === i" :disabled="i > maxStep" @click="step = i">
          {{ i + 1 }}. {{ s }}
        </b-nav-item>
      </b-nav>

      <div v-if="!draft" class="text-center py-3"><b-spinner /></div>

      <b-row v-else>
        <b-col lg="8">
          <!-- 1. Şablon -->
          <div v-show="step === 0">
            <div
              v-for="t in templates"
              :key="t.key"
              class="border rounded p-1 mb-1 border-success bg-light-success"
            >
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">{{ t.name }}</h5>
                <b-badge v-if="t.recommended" variant="success">önerilen</b-badge>
              </div>
              <small class="text-muted d-block mb-50">{{ t.description }}</small>
              <small>12 slot · 3400 USDT · 4x · Sabit · motorlar: Climax, mom-F, I5 (≤4), J1 (≤2), MB-v2 (≤3), TMA-short (≤2) · havuz serbest</small>
            </div>
            <b-form-group label="Filo adı" class="mt-1">
              <b-form-input v-model="draft.name" maxlength="120" />
            </b-form-group>
            <small class="text-muted">Sonraki adımlarda istediğin ayarı değiştirebilirsin; test edilen ayardan ayrılan her alan işaretlenir.</small>
          </div>

          <!-- 2. Sermaye ve risk -->
          <div v-show="step === 1">
            <risk-form v-model="draft" :show-mode="false" />
          </div>

          <!-- 3. Motorlar ve slotlar -->
          <div v-show="step === 2">
            <engine-slots-editor v-model="draft" :tested="testedEngines" @reset-engines="resetEngines" />
          </div>

          <!-- 4. Mod ve özet -->
          <div v-show="step === 3">
            <risk-form
              v-model="draft"
              :locked="summaryLock"
              :api-keys="apiKeys"
              :live-enabled="flags.liveEnabled"
            />
            <b-alert show variant="secondary">
              <div class="p-1">
                <small>Filo <strong>Taslak</strong> olarak oluşturulur ve {{ draft.slotCount }} slot botu açılır. İşlem yapması için detay ekranından <strong>Başlat</strong> demen gerekir.</small>
              </div>
            </b-alert>
          </div>

          <div class="d-flex justify-content-between mt-2">
            <b-button variant="outline-secondary" :disabled="step === 0" @click="step -= 1">Geri</b-button>
            <b-button v-if="step < steps.length - 1" variant="primary" @click="next">İleri</b-button>
            <b-button v-else variant="success" :disabled="busy || hasErrors" @click="onCreate">
              <b-spinner v-if="busy" small /> Oluştur (Taslak)
            </b-button>
          </div>
        </b-col>

        <b-col lg="4">
          <b-card no-body class="border mb-0">
            <b-card-body>
              <h6>Özet</h6>
              <ul class="list-unstyled mb-1">
                <li><small class="text-muted">Bütçe:</small> {{ usd(draft.budget) }} <small class="text-muted">(slot başı {{ usd(draft.budget / Math.max(1, draft.slotCount)) }})</small></li>
                <li><small class="text-muted">Slot:</small> {{ draft.slotCount }} · <small class="text-muted">Kaldıraç:</small> {{ draft.leverage }}x</li>
                <li><small class="text-muted">Boyutlama:</small> {{ sizingLabel }}</li>
                <li><small class="text-muted">Açık motorlar:</small> {{ enabledEngines }}</li>
                <li><small class="text-muted">Mod:</small> {{ draft.mode }}</li>
              </ul>
              <check-panel :check="check" :expected="expected" />
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BNav, BNavItem, BButton, BBadge, BAlert, BSpinner, BFormGroup, BFormInput,
} from 'bootstrap-vue'
import axiosIns from '@/libs/axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import RiskForm from './components/RiskForm.vue'
import EngineSlotsEditor from './components/EngineSlotsEditor.vue'
import CheckPanel from './components/CheckPanel.vue'
import {
  ENGINE_KEYS, ENGINE_INFO, SIZING_INFO, formatUsd, cloneDraft,
} from './fleetFormat'

export default {
  name: 'FleetCreate',
  components: {
    BCard, BCardBody, BRow, BCol, BNav, BNavItem, BButton, BBadge, BAlert, BSpinner, BFormGroup, BFormInput, RiskForm, EngineSlotsEditor, CheckPanel,
  },
  data() {
    return {
      steps: ['Şablon ve ad', 'Sermaye ve risk', 'Motorlar ve slotlar', 'Mod ve özet'],
      step: 0,
      maxStep: 0,
      draft: null,
      check: { errors: [], warnings: [], tested: true },
      expected: null,
      apiKeys: [],
      previewTimer: null,
    }
  },
  computed: {
    templates() { return this.$store.getters['fleet/templates'] },
    flags() { return this.$store.getters['fleet/flags'] },
    busy() { return this.$store.getters['fleet/busy'] },
    saveStatus() { return this.$store.getters['fleet/dataSaveStatus'] },
    testedEngines() { return this.templates.length ? this.templates[0].draft.engines : {} },
    hasErrors() { return !!(this.check.errors && this.check.errors.length) },
    sizingLabel() { return (SIZING_INFO[this.draft.sizingMode] || {}).label },
    enabledEngines() { return ENGINE_KEYS.filter(e => this.draft.engines[e] && this.draft.engines[e].enabled).map(e => ENGINE_INFO[e].label).join(', ') || '—' },
    summaryLock() {
      return {
        budget: 'önceki adımda', slotCount: 'önceki adımda', leverage: 'önceki adımda', sizingMode: 'önceki adımda',
      }
    },
  },
  watch: {
    draft: { deep: true, handler() { this.schedulePreview() } },
    step(v) { if (v > this.maxStep) this.maxStep = v },
    saveStatus(val) {
      if (val.status === false) this.toast('danger', 'Hata', val.message)
    },
  },
  created() {
    Promise.all([this.$store.dispatch('fleet/getTemplates'), this.$store.dispatch('fleet/getList')]).then(() => {
      if (this.templates.length) {
        const d = cloneDraft(this.templates[0].draft)
        if (this.$store.getters['fleet/list'].some(f => f.name === d.name)) d.name = `${d.name} ${this.$store.getters['fleet/list'].length + 1}`
        this.draft = d
      }
    })
    axiosIns.post('/api-keys', { limit: 100 })
      .then(r => {
        this.apiKeys = Array.isArray(r.data.data) ? r.data.data : []
      })
      .catch(() => { this.apiKeys = [] })
  },
  beforeDestroy() {
    if (this.previewTimer) clearTimeout(this.previewTimer)
  },
  methods: {
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(this.runPreview, 300)
    },
    runPreview() {
      if (!this.draft) return
      this.$store.dispatch('fleet/preview', this.draft)
        .then(r => { this.check = r.check; this.expected = r.expected })
        .catch(e => {
          const d = e && e.response && e.response.data
          const errs = d && d.errors ? Object.entries(d.errors).map(([k, v]) => `${k}: ${[].concat(v).join(', ')}`) : [(d && d.message) || 'Ayar kontrol edilemedi.']
          this.check = {
            errors: errs, warnings: [], tested: false, untested: [],
          }
          this.expected = null
        })
    },
    resetEngines() {
      this.draft = { ...this.draft, engines: cloneDraft(this.testedEngines) }
    },
    next() { this.step += 1 },
    onCreate() {
      this.$store.dispatch('fleet/create', this.draft)
        .then(d => {
          this.toast('success', 'Tamam', 'Filo Taslak olarak oluşturuldu.')
          if (d && d.fleet) this.$router.push({ name: 'FleetDetail', params: { id: d.fleet.id } })
        })
        .catch(() => {})
    },
    usd: v => formatUsd(v),
    toast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, icon: variant === 'success' ? 'CheckIcon' : 'XIcon', text, variant,
        },
      })
    },
  },
}
</script>
